"use client";

import type { BlogForClient } from "@/features/blog/shared/types";
import { Link } from "@/i18n/navigation";
import { Image } from "@/lib/client/components/Image";
import { AspectRatio, Badge, HStack, Text, VStack } from "@/ui";

interface BlogItemProps {
  post: BlogForClient;
}

/**
 * BlogItem component - Individual blog post card
 * Modern minimal design with subtle hover effects
 */
export const BlogItem = ({ post }: BlogItemProps) => {
  return (
    <Link
      href={`/blog/${post.slug}`}
      className="group block w-full cursor-pointer break-inside-avoid"
    >
      <VStack className="overflow-hidden rounded-lg bg-card transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-lg">
        {/* Image container */}
        <div className="relative overflow-hidden">
          <AspectRatio ratio={4 / 3}>
            <Image
              src={post.headerImage.src}
              alt={post.headerImage.alt || post.title}
              fill
              sizes="(max-width: 1024px) 100vw, 33vw"
              className="h-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-[1.03]"
            />
          </AspectRatio>
        </div>

        {/* Content */}
        <VStack className="space-y-3 p-6">
          {/* Meta row: Date and Category */}
          <HStack className="items-center justify-between text-xs">
            <Text className="text-muted-foreground">
              {new Date(post.updatedAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </Text>
            {post.categories?.[0] && (
              <Badge
                variant="outline"
                className="rounded-full border-primary/30 px-2.5 py-0.5 text-xs"
              >
                {post.categories[0]}
              </Badge>
            )}
          </HStack>

          {/* Title - Serif typography, 2-line clamp */}
          <Text
            as="h3"
            className="font-serif text-lg font-medium leading-snug tracking-tight line-clamp-2"
          >
            {post.title}
          </Text>
        </VStack>
      </VStack>
    </Link>
  );
};
